import * as THREE from 'three';
import type { MapDefinition } from '../../../shared/game/world-types.js';
import { caveAt, levelHeight } from '../../../shared/game/caves.js';
import { caveWindow, terrainHeight, terrainMesh } from '../../../shared/game/terrain.js';

/**
 * Presentation of a map's caves under their roofs (`shared/game/caves.ts`). A metre grid over the roofs says, for each cell,
 * whether a cave lies under the rock, how high its ceiling is (the floor nearby plus the cave's clear height), and how much sky
 * reaches its floor. Every lit material reads it: under the rock and below the ceiling there is no sun and, away from the
 * mouths and fallen-in chambers, almost no sky, so a cave is dark until it is given lights of its own. The ground reads it too:
 * the surface's rock is open below the ceiling where a mouth leads in, the cave's floor is drawn only under the roof, and the
 * roof dissolves while the viewer's heroes are inside (with the rock sunk round them by `WallsDown`).
 */

/** Sky reaching a cave's floor far from any opening, and how fast it falls off inside one (metres). */
const SKY_FLOOR = .07, SKY_REACH = 5;
/** The ceiling is set over the lowest floor within this many cells, so a wall's foot belongs to the room it rises from. */
const FLOOR_REACH = 3;
/** A mouth's rock face, and the open ground just outside it, lie within this many cells of the rock's edge. */
const MOUTH_CELLS = 2;
const CELL = 1, CUT_SECONDS = .45;

/** Uniform values every lit program shares, whichever material it is: `clone` hands back the same object (three clones a shader's uniforms per material). */
class SharedVector4 extends THREE.Vector4 { override clone() { return this; } }
class SharedTexture extends THREE.DataTexture { override clone() { return this; } }
const empty = new Float32Array(4);
const caveSkyMap = new SharedTexture(empty, 1, 1, THREE.RGBAFormat, THREE.FloatType);
/** x0, z0, cells per metre, on. */
const caveSkyBounds = new SharedVector4();
/** columns, rows, roof cut (0-1). */
const caveSkySize = new SharedVector4(1, 1, 0, 0);

const SAMPLE = `
uniform highp sampler2D caveSkyMap;
uniform vec4 caveSkyBounds;
uniform vec4 caveSkySize;
// x: within reach of a cave's rock (under it, or at a mouth), y: the ceiling's height there, z: the sky reaching the floor,
// w: under the rock; bilinear over the metre grid.
vec4 caveSkyCell( vec2 xz ) {
  if ( caveSkyBounds.w < .5 ) return vec4( 0., -1e9, 1., 0. );
  vec2 cell = ( xz - caveSkyBounds.xy ) * caveSkyBounds.z;
  if ( any( lessThan( cell, vec2( 0. ) ) ) || any( greaterThanEqual( cell, caveSkySize.xy - 1. ) ) ) return vec4( 0., -1e9, 1., 0. );
  ivec2 i = ivec2( floor( cell ) ); vec2 f = cell - floor( cell );
  vec4 a = texelFetch( caveSkyMap, i, 0 ), b = texelFetch( caveSkyMap, i + ivec2( 1, 0 ), 0 );
  vec4 c = texelFetch( caveSkyMap, i + ivec2( 0, 1 ), 0 ), d = texelFetch( caveSkyMap, i + ivec2( 1, 1 ), 0 );
  return mix( mix( a, b, f.x ), mix( c, d, f.x ), f.y );
}
`;

const DECLARATIONS = SAMPLE + `
// How much of the sun and sky reaches a point given in view space.
float caveSkyAt( vec3 viewPosition ) {
  if ( caveSkyBounds.w < .5 ) return 1.;
  vec3 world = ( ( vec4( viewPosition, 1. ) - viewMatrix[ 3 ] ) * viewMatrix ).xyz;
  vec4 cell = caveSkyCell( world.xz );
  return mix( 1., cell.z, 1. - smoothstep( cell.y - .75, cell.y + .25, world.y ) );
}
`;

/** Installed once, before any program compiles: every lit material's sun and sky light passes through `caveSkyAt`. */
function install() {
  const chunks = THREE.ShaderChunk;
  if (chunks.lights_pars_begin.includes('caveSkyAt')) return;
  const begin = chunks.lights_fragment_begin, position = 'vec3 geometryPosition = - vViewPosition;';
  const lights = ['getDirectionalLightInfo( directionalLight, directLight );', 'getSunLightInfo( sunLight, directLight );'];
  if (!begin.includes(position) || !begin.includes(lights[0])) { console.warn('Cave light: three\'s lighting chunk changed; caves are lit as the open ground.'); return; }
  chunks.lights_pars_begin += DECLARATIONS;
  let patched = begin.replace(position, `${position}\nfloat caveSky = caveSkyAt( geometryPosition );`);
  for (const light of lights) patched = patched.replace(light, `${light}\ndirectLight.color *= caveSky;`);
  chunks.lights_fragment_begin = patched;
  chunks.lights_fragment_end = `
#if defined( RE_IndirectDiffuse )
  irradiance *= caveSky; iblIrradiance *= caveSky;
#endif
#if defined( RE_IndirectSpecular )
  radiance *= caveSky;
#endif
` + chunks.lights_fragment_end;
  for (const name of ['standard', 'physical', 'lambert', 'phong', 'toon'] as const)
    Object.assign(THREE.ShaderLib[name].uniforms, { caveSkyMap: { value: caveSkyMap }, caveSkyBounds: { value: caveSkyBounds }, caveSkySize: { value: caveSkySize } });
}
install();

interface Grid { x0: number; z0: number; columns: number; rows: number; under: Uint8Array; mouth: Uint8Array; ceiling: Float32Array; floor: Float32Array; level: Uint8Array }

export class CaveView {
  private grid: Grid | null = null;
  private amount = 0;
  private adopted = new WeakSet<THREE.Material>();

  constructor(private map: MapDefinition) {
    caveSkyBounds.w = 0; caveSkySize.z = 0;
    if (!map.caves?.length || !map.terrain?.roofs?.length) return;
    const grid = this.grid = buildGrid(map);
    const { columns, rows, under, mouth, ceiling } = grid, sky = skyReach(map, grid), data = new Float32Array(columns * rows * 4);
    for (let k = 0; k < under.length; k++) { data[k * 4] = mouth[k]; data[k * 4 + 1] = mouth[k] ? ceiling[k] : -1e4; data[k * 4 + 2] = sky[k]; data[k * 4 + 3] = under[k]; }
    // The shared texture keeps its identity across maps; a new grid replaces its data.
    caveSkyMap.dispose();
    caveSkyMap.image = { data, width: columns, height: rows };
    caveSkyMap.needsUpdate = true;
    caveSkyBounds.set(grid.x0, grid.z0, 1 / CELL, 1);
    caveSkySize.set(columns, rows, 0, 0);
  }

  /** How far the roofs are cut away (0-1), following the sunk rock while the viewer's heroes are inside. */
  get cut() { return this.amount; }

  /** Follows `WallsDown`: the rock sinks round heroes inside and the roof over them dissolves with it. */
  update(dt: number, inside: boolean) {
    if (!this.grid) return;
    this.amount = THREE.MathUtils.clamp(this.amount + (inside ? dt : -dt) / CUT_SECONDS, 0, 1);
    caveSkySize.z = this.amount * this.amount * (3 - 2 * this.amount);
  }

  /** The cave level under the rock at a point, or 0. */
  under(x: number, z: number) {
    const grid = this.grid;
    if (!grid) return 0;
    const i = Math.round((x - grid.x0) / CELL), j = Math.round((z - grid.z0) / CELL);
    return i < 0 || j < 0 || i >= grid.columns || j >= grid.rows ? 0 : grid.under[j * grid.columns + i] ? grid.level[j * grid.columns + i] : 0;
  }

  /** Whether a point lies beside the rock's edge, outside it: a cave's floor is drawn there below the ceiling, where a mouth opens. */
  nearMouth(x: number, z: number) {
    const grid = this.grid;
    if (!grid) return false;
    const i = Math.round((x - grid.x0) / CELL), j = Math.round((z - grid.z0) / CELL);
    return i >= 0 && j >= 0 && i < grid.columns && j < grid.rows && !!grid.mouth[j * grid.columns + i] && !grid.under[j * grid.columns + i];
  }

  /** The level a picked ground point is on: under the rock, the cave below its ceiling, otherwise the surface. */
  levelAt(x: number, z: number, y: number) {
    const grid = this.grid, level = this.under(x, z);
    if (!grid || !level) return 0;
    const i = Math.round((x - grid.x0) / CELL), j = Math.round((z - grid.z0) / CELL);
    return y < grid.ceiling[j * grid.columns + i] ? level : 0;
  }

  /** The ground drawn at a point for anything without a level of its own (a blow, a footprint): the cave's floor while the roof over it is cut, otherwise the top. */
  groundHeight(x: number, z: number) {
    const level = this.amount > .5 ? this.under(x, z) : 0;
    return levelHeight(this.map, x, z, level);
  }

  /**
   * The ground's own drawing: `caveLayer` is 0 on the surface and 1 on a cave's floor. Below the ceiling, at the rock's edge and
   * under it, the floor is drawn and the surface is not, so a mouth opens into the cave; above it the surface is the rock's top,
   * which dissolves while the roof is cut, the floor's walls showing in its place.
   */
  adoptGround(material: THREE.Material) {
    if (!this.grid || this.adopted.has(material)) return;
    this.adopted.add(material);
    const compile = material.onBeforeCompile, key = material.customProgramCacheKey.call(material);
    material.onBeforeCompile = function (this: THREE.Material, shader: THREE.WebGLProgramParametersWithUniforms, renderer: THREE.WebGLRenderer) {
      compile.call(this, shader, renderer);
      shader.vertexShader = 'attribute float caveLayer; varying float vCaveLayer; varying vec3 vCaveWorld;\n' + shader.vertexShader
        .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vCaveLayer = caveLayer; vCaveWorld = (modelMatrix * vec4(position, 1.)).xyz;');
      shader.fragmentShader = 'varying float vCaveLayer; varying vec3 vCaveWorld;\n' + shader.fragmentShader
        .replace('#include <clipping_planes_fragment>', `#include <clipping_planes_fragment>
  {
    vec4 caveCell = caveSkyCell(vCaveWorld.xz);
    bool caveOpen = caveCell.x > .5 && vCaveWorld.y < caveCell.y - .05;
    bool caveCut = caveCell.w > .5 && fract(sin(dot(floor(vCaveWorld.xz * 4.), vec2(12.9898, 78.233))) * 43758.5453) < caveSkySize.z;
    if (vCaveLayer > .5 ? !(caveOpen || caveCut) : caveOpen || caveCut) discard;
  }`);
    };
    material.customProgramCacheKey = () => key + '|cave-view';
    material.needsUpdate = true;
  }

  /** Scenery on a roof's top (stones, grass, trees) dissolves with it while it is cut; scenery down in the cave stays. */
  adoptScenery(root: THREE.Object3D) {
    if (!this.grid) return;
    const adopted = this.adopted;
    root.traverse(object => {
      if (!(object instanceof THREE.Mesh)) return;
      for (const material of Array.isArray(object.material) ? object.material : [object.material]) {
        if (adopted.has(material) || material.transparent || !material.colorWrite) continue;
        adopted.add(material);
        const compile = material.onBeforeCompile, key = material.customProgramCacheKey.call(material);
        material.onBeforeCompile = function (this: THREE.Material, shader: THREE.WebGLProgramParametersWithUniforms, renderer: THREE.WebGLRenderer) {
          compile.call(this, shader, renderer);
          if (!shader.fragmentShader.includes('#include <clipping_planes_fragment>') || !shader.vertexShader.includes('mvPosition')) return;
          // Unlit programs lack the lighting declarations, so they bring their own.
          if (!shader.fragmentShader.includes('#include <lights_pars_begin>')) {
            Object.assign(shader.uniforms, { caveSkyMap: { value: caveSkyMap }, caveSkyBounds: { value: caveSkyBounds }, caveSkySize: { value: caveSkySize } });
            shader.fragmentShader = SAMPLE + shader.fragmentShader;
          }
          // World position from the final view position, after instancing, batching and wind.
          const end = shader.vertexShader.lastIndexOf('}');
          shader.vertexShader = 'varying vec3 vCaveTop;\n' + shader.vertexShader.slice(0, end)
            + '\nvCaveTop = (vec4(mvPosition.xyz - viewMatrix[3].xyz, 0.) * viewMatrix).xyz;\n}';
          shader.fragmentShader = 'varying vec3 vCaveTop;\n' + shader.fragmentShader
            .replace('#include <clipping_planes_fragment>', `#include <clipping_planes_fragment>
  if (caveSkySize.z > 0.) {
    vec4 caveCell = caveSkyCell(vCaveTop.xz);
    if (caveCell.w > .5 && vCaveTop.y > caveCell.y - .05 && fract(sin(dot(floor(vCaveTop.xz * 4.), vec2(12.9898, 78.233))) * 43758.5453) < caveSkySize.z) discard;
  }`);
        };
        material.customProgramCacheKey = () => key + '|cave-view';
        material.needsUpdate = true;
      }
    });
  }

  dispose() {
    if (this.grid) { caveSkyBounds.w = 0; caveSkySize.z = 0; }
  }
}

/** The metre grid over every cave's window: which cells lie under the rock, on which level, and how high each ceiling is. */
function buildGrid(map: MapDefinition): Grid {
  const mesh = terrainMesh(map), windows = map.caves!.map((_, index) => caveWindow(map, index + 1)).filter(window => !!window);
  const x0 = Math.floor(Math.min(...windows.map(w => w.i0 * mesh.dx)) - map.width / 2), z0 = Math.floor(Math.min(...windows.map(w => w.j0 * mesh.dz)) - map.depth / 2);
  const columns = Math.ceil(Math.max(...windows.map(w => w.i1 * mesh.dx)) - map.width / 2 - x0) + 1, rows = Math.ceil(Math.max(...windows.map(w => w.j1 * mesh.dz)) - map.depth / 2 - z0) + 1;
  const under = new Uint8Array(columns * rows), mouth = new Uint8Array(columns * rows), level = new Uint8Array(columns * rows), floor = new Float32Array(columns * rows), ceiling = new Float32Array(columns * rows);
  for (let j = 0; j < rows; j++) for (let i = 0; i < columns; i++) {
    const k = j * columns + i, x = x0 + i * CELL, z = z0 + j * CELL, cave = caveAt(map, x, z);
    under[k] = cave ? 1 : 0; level[k] = cave;
    floor[k] = cave ? levelHeight(map, x, z, cave) : Infinity;
  }
  // Under the rock and within reach of it, so a mouth's face and the ground just outside it go with the cave.
  for (let j = 0; j < rows; j++) for (let i = 0; i < columns; i++) {
    if (!under[j * columns + i]) continue;
    for (let dj = -MOUTH_CELLS; dj <= MOUTH_CELLS; dj++) for (let di = -MOUTH_CELLS; di <= MOUTH_CELLS; di++) {
      const ni = i + di, nj = j + dj;
      if (ni >= 0 && nj >= 0 && ni < columns && nj < rows && !level[nj * columns + ni]) { mouth[nj * columns + ni] = 1; level[nj * columns + ni] = level[j * columns + i] | 128; }
    }
    mouth[j * columns + i] = 1;
  }
  for (let k = 0; k < level.length; k++) level[k] &= 127;
  // The lowest floor near each cell, in two separable passes, plus that cave's clear height.
  const across = new Float32Array(floor.length);
  for (let j = 0; j < rows; j++) for (let i = 0; i < columns; i++) {
    let low = Infinity;
    for (let n = Math.max(0, i - FLOOR_REACH); n <= Math.min(columns - 1, i + FLOOR_REACH); n++) low = Math.min(low, floor[j * columns + n]);
    across[j * columns + i] = low;
  }
  for (let j = 0; j < rows; j++) for (let i = 0; i < columns; i++) {
    const k = j * columns + i;
    if (!mouth[k]) continue;
    let low = Infinity;
    for (let n = Math.max(0, j - FLOOR_REACH); n <= Math.min(rows - 1, j + FLOOR_REACH); n++) low = Math.min(low, across[n * columns + i]);
    // Just outside a mouth the floor beside it is the cave's own, so the ceiling runs on out to the rock's face.
    ceiling[k] = low < Infinity ? low + map.caves![level[k] - 1].ceiling : -1e4;
  }
  return { x0, z0, columns, rows, under, mouth, level, floor, ceiling };
}

/**
 * Sky reaching each cell: all of it in the open, and under the rock falling off with the distance walked in from the nearest
 * opening (open ground beside the roof that lies below its ceiling: a mouth or a fallen-in chamber), never through rock.
 */
function skyReach(map: MapDefinition, grid: Grid) {
  const { columns, rows, under, ceiling, x0, z0 } = grid, distance = new Float32Array(under.length).fill(Infinity);
  for (let j = 0; j < rows; j++) for (let i = 0; i < columns; i++) {
    const k = j * columns + i;
    if (under[k]) continue;
    let low = Infinity;
    for (let dj = -FLOOR_REACH; dj <= FLOOR_REACH; dj++) for (let di = -FLOOR_REACH; di <= FLOOR_REACH; di++) {
      const n = (j + dj) * columns + i + di;
      if (i + di >= 0 && j + dj >= 0 && i + di < columns && j + dj < rows && under[n]) low = Math.min(low, ceiling[n]);
    }
    if (low < Infinity && terrainHeight(map, x0 + i * CELL, z0 + j * CELL) < low) distance[k] = 0;
  }
  const relax = (k: number, from: number, cost: number) => { if (under[k] && distance[from] + cost < distance[k]) distance[k] = distance[from] + cost; };
  for (let j = 0; j < rows; j++) for (let i = 0; i < columns; i++) {
    const k = j * columns + i;
    if (i > 0) relax(k, k - 1, CELL);
    if (j > 0) { relax(k, k - columns, CELL); if (i > 0) relax(k, k - columns - 1, CELL * Math.SQRT2); if (i < columns - 1) relax(k, k - columns + 1, CELL * Math.SQRT2); }
  }
  for (let j = rows - 1; j >= 0; j--) for (let i = columns - 1; i >= 0; i--) {
    const k = j * columns + i;
    if (i < columns - 1) relax(k, k + 1, CELL);
    if (j < rows - 1) { relax(k, k + columns, CELL); if (i < columns - 1) relax(k, k + columns + 1, CELL * Math.SQRT2); if (i > 0) relax(k, k + columns - 1, CELL * Math.SQRT2); }
  }
  const sky = new Float32Array(under.length);
  for (let k = 0; k < sky.length; k++) sky[k] = under[k] ? SKY_FLOOR + (1 - SKY_FLOOR) * Math.exp(-distance[k] / SKY_REACH) : 1;
  return sky;
}
